// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * ui.ts — small DOM helpers. Deliberately tiny: the screens live in main.ts
 * where the state they read lives, and a "component framework" for six screens
 * would be more indirection than the game has complexity.
 */

export function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  html?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (html !== undefined) node.innerHTML = html;
  return node;
}

/** Escape anything that reaches innerHTML — player names arrive over the wire. */
export function esc(s: unknown): string {
  return String(s ?? '').replace(
    /[&<>"']/g,
    (c) =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c] ?? c,
  );
}

export function on<K extends keyof HTMLElementEventMap>(
  target: HTMLElement | Document | Window,
  type: K,
  fn: (e: HTMLElementEventMap[K]) => void,
  opts?: AddEventListenerOptions,
): () => void {
  const handler = fn as EventListener;
  target.addEventListener(type, handler, opts);
  return () => target.removeEventListener(type, handler, opts);
}

/**
 * A modal dialog that is actually reachable: focus moves into it, Escape closes
 * it, and focus returns to whatever opened it.
 */
export function openModal(title: string, bodyHtml: string): void {
  const prev = document.activeElement as HTMLElement | null;
  const wrap = el('div', 'modal-wrap');
  wrap.innerHTML = `
    <div class="modal" role="dialog" aria-modal="true" aria-label="${esc(title)}">
      <h2 class="modal-title">${esc(title)}</h2>
      <div class="modal-body">${bodyHtml}</div>
      <button class="btn btn-primary modal-close" type="button">Close</button>
    </div>`;
  document.body.appendChild(wrap);

  const close = (): void => {
    wrap.remove();
    document.removeEventListener('keydown', onKey);
    prev?.focus?.();
  };
  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') close();
  }
  document.addEventListener('keydown', onKey);
  wrap.querySelector<HTMLButtonElement>('.modal-close')?.addEventListener('click', close);
  wrap.addEventListener('click', (e) => {
    if (e.target === wrap) close();
  });
  wrap.querySelector<HTMLButtonElement>('.modal-close')?.focus();
}

/** Brief non-blocking message — a rejected placement, a copied link. */
let toastTimer: ReturnType<typeof setTimeout> | undefined;
export function toast(message: string, tone: 'info' | 'bad' = 'info'): void {
  let node = document.querySelector<HTMLElement>('.toast');
  if (!node) {
    node = el('div', 'toast');
    node.setAttribute('role', 'status');
    node.setAttribute('aria-live', 'polite');
    document.body.appendChild(node);
  }
  node.textContent = message;
  node.classList.toggle('toast-bad', tone === 'bad');
  node.classList.add('toast-on');
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => node?.classList.remove('toast-on'), 1600);
}
